package se.mittpass.web.pages

import se.mittpass.web.*
import se.mittpass.web.components.*
import se.mittpass.web.pages.*
import se.mittpass.web.viewmodel.*
import se.mittpass.web.style.*


import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.attributes.*
import se.mittpass.shared.api.MessageDto
import se.mittpass.shared.api.CreateMessageRequest

private val msgScope = MainScope()

@Composable
fun MessagesPage(workplaceId: Long, isManager: Boolean, currentUserId: Long? = null) {
    var messages by remember { mutableStateOf<List<MessageDto>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var newMessage by remember { mutableStateOf("") }
    var sending by remember { mutableStateOf(false) }

    fun loadMessages() {
        loading = true
        msgScope.launch {
            messageRepo.getMessages(workplaceId).onSuccess { messages = it }
            loading = false
        }
    }

    LaunchedEffect(messages) {
        kotlinx.browser.window.setTimeout({
            val el = kotlinx.browser.document.getElementById("chat-stream-container")
            if (el != null) {
                el.asDynamic().scrollTop = el.asDynamic().scrollHeight
            }
        }, 50)
    }

    Div({
        style {
            property("display", "flex")
            property("flex-direction", "column")
            property("gap", "1rem")
            property("max-width", "900px")
            property("margin", "0 auto")
        }
    }) {

        // Header
        Div({
            style {
                property("background", "var(--rp-surface)")
                property("border-radius", "16px")
                property("padding", "1rem 1.5rem")
                property("display", "flex")
                property("justify-content", "space-between")
                property("align-items", "center")
                property("border", "1.5px solid var(--rp-hl-med)")
                property("box-shadow", "0 4px 12px rgba(15,23,42,0.04)")
            }
        }) {
            Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.75rem") } }) {
                Div({
                    style {
                        property("width", "42px"); property("height", "42px"); property("border-radius", "12px")
                        property("background", "linear-gradient(135deg, var(--rp-pine), var(--rp-iris))")
                        property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
                    }
                }) {
                    FaIcon("fa-solid fa-comments", size = "1.1rem", color = "white")
                }
                Div {
                    H3({ style { property("margin", "0"); property("font-size", "1.2rem"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) {
                        Text("Teamchatt")
                    }
                    Span({ style { property("color", "var(--rp-subtle)"); property("font-size", "0.8rem"); property("font-weight", "500") } }) {
                        Text("Intern kommunikation för arbetsplatsen")
                    }
                }
            }
            Span({
                style {
                    property("background", "var(--rp-overlay)")
                    property("color", "var(--rp-text)")
                    property("border", "1px solid var(--rp-hl-med)")
                    property("padding", "0.35rem 0.85rem")
                    property("border-radius", "20px")
                    property("font-size", "0.8rem")
                    property("font-weight", "700")
                }
            }) {
                Text("${messages.size} meddelanden")
            }
        }

        // Pinned messages banner
        val pinned = messages.filter { it.pinned }
        if (pinned.isNotEmpty()) {
            Div({
                style {
                    property("background", "var(--rp-warn-bg)")
                    property("border-radius", "16px")
                    property("padding", "1rem 1.25rem")
                    property("border", "1.5px solid var(--rp-warn-border)")
                }
            }) {
                Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem"); property("margin-bottom", "0.5rem") } }) {
                    FaIcon("fa-solid fa-thumbtack", size = "0.9rem", color = "var(--rp-warn-text)")
                    Span({ style { property("font-weight", "800"); property("font-size", "0.8rem"); property("color", "var(--rp-warn-text)"); property("text-transform", "uppercase"); property("letter-spacing", "0.5px") } }) {
                        Text("Fästa meddelanden")
                    }
                }
                pinned.forEach { msg ->
                    Div({ style { property("padding", "0.4rem 0"); property("color", "var(--rp-warn-text)"); property("font-size", "0.9rem"); property("font-weight", "600") } }) {
                        Text("• ${msg.senderName}: ${msg.content}")
                    }
                }
            }
        }

        // Chat messages stream container
        Div({
            id("chat-stream-container")
            style {
                property("background", "var(--rp-overlay)")
                property("border-radius", "20px")
                property("padding", "1.5rem")
                property("min-height", "380px")
                property("max-height", "550px")
                property("overflow-y", "auto")
                property("display", "flex")
                property("flex-direction", "column")
                property("gap", "1rem")
                property("border", "1.5px solid var(--rp-hl-med)")
                property("box-shadow", "0 4px 16px rgba(15,23,42,0.04)")
            }
        }) {
            if (loading) {
                Div({ style { property("text-align", "center"); property("padding", "3rem"); property("color", "var(--rp-subtle)") } }) {
                    Text("Laddar meddelanden…")
                }
            } else if (messages.isEmpty()) {
                Div({ style { property("text-align", "center"); property("padding", "4rem 1rem"); property("color", "var(--rp-subtle)") } }) {
                    Div({ style { property("margin-bottom", "0.75rem") } }) {
                        FaIcon("fa-solid fa-comments", size = "2.5rem", color = "var(--rp-muted)")
                    }
                    Div({ style { property("font-weight", "700"); property("font-size", "1.1rem"); property("color", "var(--rp-text)") } }) { Text("Inga meddelanden än") }
                    Div({ style { property("font-size", "0.9rem"); property("margin-top", "0.35rem"); property("color", "var(--rp-subtle)") } }) { Text("Skriv det första meddelandet till ditt team nedan!") }
                }
            } else {
                val sortedMessages = messages.sortedBy { it.createdAt }
                sortedMessages.forEach { msg ->
                    val isOwn = currentUserId != null && msg.senderUserId == currentUserId
                    MessageBubbleRow(msg, isOwn = isOwn, isManager = isManager, onRefresh = ::loadMessages)
                }
            }
        }

        // Input Bar (Bottom)
        Div({
            style {
                property("background", "var(--rp-surface)")
                property("border-radius", "20px")
                property("padding", "0.85rem 1.25rem")
                property("display", "flex")
                property("gap", "0.75rem")
                property("align-items", "center")
                property("border", "1.5px solid var(--rp-hl-med)")
                property("box-shadow", "0 4px 12px rgba(15,23,42,0.04)")
            }
        }) {
            Input(type = InputType.Text) {
                value(newMessage)
                onInput { newMessage = (it.target.asDynamic().value as? String) ?: "" }
                attr("placeholder", "Skriv ett meddelande till teamet...")
                style {
                    property("flex", "1")
                    property("padding", "0.85rem 1.25rem")
                    property("border", "1.5px solid var(--rp-hl-med)")
                    property("border-radius", "12px")
                    property("font-size", "0.95rem")
                    property("outline", "none")
                    property("font-family", "inherit")
                    property("background", "var(--rp-surface)")
                    property("transition", "all 0.2s ease")
                    property("color", "var(--rp-text)")
                }
            }
            Button({
                onClick {
                    if (newMessage.isBlank() || sending) return@onClick
                    sending = true
                    msgScope.launch {
                        messageRepo.sendMessage(workplaceId, CreateMessageRequest(newMessage))
                            .onSuccess { newMessage = ""; loadMessages() }
                        sending = false
                    }
                }
                if (sending || newMessage.isBlank()) disabled()
                style {
                    property("width", "46px")
                    property("height", "46px")
                    property("border", "none")
                    property("background", if (newMessage.isBlank()) "var(--rp-hl-med)" else "linear-gradient(135deg, var(--rp-pine), var(--rp-iris))")
                    property("color", "white")
                    property("border-radius", "12px")
                    property("display", "flex")
                    property("align-items", "center")
                    property("justify-content", "center")
                    property("cursor", if (newMessage.isBlank()) "default" else "pointer")
                    property("font-size", "1rem")
                    property("flex-shrink", "0")
                    property("transition", "all 0.2s ease")
                    property("box-shadow", if (newMessage.isBlank()) "none" else "0 4px 12px rgba(108,60,225,0.3)")
                }
            }) {
                FaIcon("fa-solid fa-paper-plane", size = "0.95rem", color = "white")
            }
        }
    }
}

@Composable
private fun MessageBubbleRow(msg: MessageDto, isOwn: Boolean, isManager: Boolean, onRefresh: () -> Unit) {
    val timeStr = formatTimestamp(msg.createdAt)
    val initials = msg.senderName.split(" ").mapNotNull { it.firstOrNull()?.toString() }.take(2).joinToString("").uppercase().ifEmpty { "?" }
    val savedAvatar = msg.senderUserId.let { uid -> kotlinx.browser.localStorage.getItem("mittpass_avatar_$uid") }

    Div({
        style {
            property("display", "flex")
            property("flex-direction", "column")
            property("align-items", if (isOwn) "flex-end" else "flex-start")
            property("width", "100%")
        }
    }) {
        // Sender name + timestamp line
        Div({
            style {
                property("display", "flex")
                property("align-items", "center")
                property("gap", "0.4rem")
                property("margin-bottom", "0.25rem")
                property("padding", "0 0.5rem")
            }
        }) {
            Span({ style { property("font-weight", "700"); property("font-size", "0.8rem"); property("color", "var(--rp-subtle)") } }) {
                Text(if (isOwn) "Du" else msg.senderName)
            }
            Span({ style { property("font-size", "0.72rem"); property("color", "var(--rp-muted)") } }) {
                Text(timeStr)
            }
        }

        // Bubble + Avatar row
        Div({
            style {
                property("display", "flex")
                property("gap", "0.5rem")
                property("align-items", "flex-end")
                property("flex-direction", if (isOwn) "row-reverse" else "row")
                property("max-width", "80%")
            }
        }) {
            // Avatar Circle
            Div({
                style {
                    property("width", "34px")
                    property("height", "34px")
                    property("border-radius", "50%")
                    if (!savedAvatar.isNullOrBlank()) {
                        property("background-image", "url('$savedAvatar')")
                        property("background-size", "cover")
                        property("background-position", "center")
                        property("border", "1.5px solid var(--rp-hl-med)")
                    } else {
                        property("background", if (isOwn) "linear-gradient(135deg, var(--rp-pine), var(--rp-iris))" else "linear-gradient(135deg, var(--rp-pine), var(--rp-pine))")
                    }
                    property("display", "flex")
                    property("align-items", "center")
                    property("justify-content", "center")
                    property("color", "white")
                    property("font-weight", "800")
                    property("font-size", "0.75rem")
                    property("flex-shrink", "0")
                    property("box-shadow", "0 2px 6px rgba(0,0,0,0.1)")
                }
            }) {
                if (savedAvatar.isNullOrBlank()) {
                    Text(initials)
                }
            }

            // Message Content Bubble
            Div({
                style {
                    property("padding", "0.85rem 1.15rem")
                    property("background", if (isOwn) "linear-gradient(135deg, var(--rp-pine), var(--rp-iris))" else "var(--rp-surface)")
                    property("color", if (isOwn) "white" else "var(--rp-text)")
                    property("border", if (isOwn) "none" else "1.5px solid var(--rp-hl-med)")
                    property("border-radius", if (isOwn) "16px 16px 4px 16px" else "16px 16px 16px 4px")
                    property("font-size", "0.93rem")
                    property("line-height", "1.45")
                    property("word-break", "break-word")
                    property("box-shadow", if (isOwn) "0 4px 12px rgba(108,60,225,0.25)" else "0 2px 8px rgba(15,23,42,0.04)")
                    property("position", "relative")
                }
            }) {
                Text(msg.content)
            }

            // Manager actions (Pin/Delete)
            if (isManager) {
                Div({
                    style {
                        property("display", "flex")
                        property("gap", "0.25rem")
                        property("align-self", "center")
                        property("opacity", "0.7")
                    }
                }) {
                    if (isManager) {
                        Button({
                            onClick { msgScope.launch { messageRepo.togglePin(msg.id).onSuccess { onRefresh() } } }
                            style { property("background", "none"); property("border", "none"); property("cursor", "pointer"); property("padding", "0.25rem") }
                            attr("title", if (msg.pinned) "Lösgör" else "Fäst")
                        }) {
                            FaIcon("fa-solid fa-thumbtack", size = "0.85rem", color = if (msg.pinned) "var(--rp-warn-text)" else "var(--rp-subtle)")
                        }
                    }
                    Button({
                        onClick { msgScope.launch { messageRepo.deleteMessage(msg.id).onSuccess { onRefresh() } } }
                        style { property("background", "none"); property("border", "none"); property("cursor", "pointer"); property("padding", "0.25rem") }
                        attr("title", "Radera")
                    }) {
                        FaIcon("fa-solid fa-trash-can", size = "0.85rem", color = "var(--rp-err-text)")
                    }
                }
            }
        }
    }
}

private fun formatTimestamp(ms: Long): String {
    val d = kotlin.js.Date(ms.toDouble())
    val h = d.getHours().toString().padStart(2, '0')
    val m = d.getMinutes().toString().padStart(2, '0')
    return "$h:$m"
}
